import { modelRegistry, type BaseModel, type EntityModel } from './registry';

/**
 * Decorator to register a model class under a given name.
 */
export function ClientModel(name: string) {
  return function (target: EntityModel) {
    modelRegistry.set(name, target);
  };
}

/**
 * Lookup decorator just looks up a value from a store by id
 * Target class does not have a foreign key, but we can lookup the value from a store
 */
export function Lookup<T>(
  getStore: () => { getById: (id: string) => T | undefined },
  fieldName: string,
) {
  return function (
    _target: BaseModel,
    _key: string,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this[fieldName];
      if (!id) return null;
      return store.getById(id) || null;
    };

    return descriptor;
  };
}
/**
 * Reference decorator (one-to-one / many-to-one) with O(1) lookup.
 * Expects target class to have instance field `<key>_id` and store to have `getById` method.
 */
export function Reference<T>(
  getStore: () => { getById: (id: string) => T | undefined },
  options: { nullable?: boolean; fk?: string } = {},
) {
  return function (
    _target: BaseModel, // unused
    key: string,
    descriptor: PropertyDescriptor,
  ) {
    const fk = options.fk || `${key}_id`;
    // Override the getter
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this[fk];
      if ((id == null || id === '') && options.nullable) return null;
      return store.getById(id) || null;
    };

    return descriptor;
  };
}

export function PolymorphicReference<T>(
  getStoreMap: () => Record<string, { getById: (id: string) => T | undefined }>,
  typeField: string,
  idField: string,
) {
  return function (
    _target: BaseModel,
    _key: string,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.get = function (this: BaseModel): T | null {
      const type = this[typeField];
      const id = this[idField];

      if (!type || !id) return null;

      const storeMap = getStoreMap();
      const store = storeMap[type];

      if (!store) return null;

      return store.getById(id) || null;
    };

    return descriptor;
  };
}

/**
 * Polymorphic one-to-many decorator with O(1) lookup for relations that can target multiple entity types.
 * Expects store to have `getByTarget(targetId: string, targetType: TTargetType) => T[]` method.
 */
export function PolymorphicOneToMany<T, TTargetType = string>(
  getStore: () => {
    getByTarget: (targetId: string, targetType: TTargetType) => T[];
  },
  targetType: TTargetType,
  sortFn?: (a: T, b: T) => number,
) {
  return function (
    _target: BaseModel,
    _key: string,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this.id;

      const arr = store.getByTarget(id, targetType);
      if (sortFn) {
        return [...arr].sort(sortFn);
      }
      return arr;
    };

    return descriptor;
  };
}

/**
 * One-to-many decorator with O(1) lookup via store index map.
 * Expects store to have `storeIndexedByForeignKey: Map<string, T[]>`.
 */
export function OneToMany<T>(
  getStore: () => { storeIndexedByForeignKey: Map<string, T[]> },
  // todo: placeholder for fixing reply sorting....tbd
  sortFn?: (a: T, b: T) => number,
) {
  return function (
    _target: BaseModel, // unused
    _key: string, // unused
    descriptor: PropertyDescriptor,
  ) {
    // Override the getter
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this.id;
      const arr = store.storeIndexedByForeignKey.get(id) || [];
      if (sortFn) {
        return [...arr].sort(sortFn);
      }
      return arr;
    };

    return descriptor;
  };
}

/**
 * One-to-one decorator with O(1) lookup via store index map.
 * Expects store to have `storeIndexedByForeignKey: Map<string, T>`.
 */
export function OneToOne<T>(
  getStore: () => { storeIndexedByForeignKey: Map<string, T> },
) {
  return function (
    _target: BaseModel, // unused
    _key: string, // unused
    descriptor: PropertyDescriptor,
  ) {
    // Override the getter
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this.id;
      return store.storeIndexedByForeignKey.get(id) || null;
    };

    return descriptor;
  };
}

/**
 * Many-to-many decorator with O(1) lookup via store index map.
 * Expects store to have `storeIndexedByForeignKey: Map<string, T[]>`.
 */
export function ManyToMany<T>(
  getStore: () => { storeIndexedByForeignKey: Map<string, T[]> },
) {
  return function (
    _target: BaseModel, // unused
    _key: string, // unused
    descriptor: PropertyDescriptor,
  ) {
    // Override the getter
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const id = this.id;
      return store.storeIndexedByForeignKey.get(id) || [];
    };

    return descriptor;
  };
}

export function UserFavorites<T>(
  getStore: () => { setOfDealIds: Set<string>; all: T[] },
) {
  return function (
    _target: BaseModel,
    _key: string,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      // Return all favorites for the current user
      return store.all;
    };

    return descriptor;
  };
}

export function IsFavorited(getStore: () => { setOfDealIds: Set<string> }) {
  return function (
    _target: BaseModel,
    _key: string,
    descriptor: PropertyDescriptor,
  ) {
    descriptor.get = function (this: BaseModel) {
      const store = getStore();
      const dealId = this.id;
      return store.setOfDealIds.has(dealId);
    };

    return descriptor;
  };
}
